<template>
  <div class="o-product-card-horizontal">
    <span class="o-product-card-horizontal__link">
      <base-link :link="product.url" class="o-product-card-horizontal__image-wrapper" @click.native="onProductClick">
        <div v-if="product.images && product.images.length">
          <base-image
            v-for="(picture, key) in product.images.slice(0, 2)"
            :key="key"
            class="o-product-card-horizontal__picture"
            :url="picture.url"
            mode="pad"
            :alt-text="picture.name"
            further-options="&bgcolor=ffffff"
            :width="131"
            :height="131"
          />
        </div>
        <base-image
          v-else
          class="o-product-card-horizontal__image"
          :url="placeholder.url"
          :alt-text="placeholder.name"
          further-options="&bgcolor=ffffff"
          :width="131"
          :height="131"
        />
        <badge v-if="isProductOnDiscount && !showAdvertisingCode" class="o-product-card-horizontal__badge bg-primary">
          {{ $t('ProductCard_Discount_Badge', 'Tilbud') }}
        </badge>
        <badge
          v-if="isSki"
          class="o-product-card-horizontal__badge bg-primary"
          :class="{ 'a-badge--offset': isProductOnDiscount && !showAdvertisingCode }"
        >
          {{ $t('ProductCard_Ski', 'SKI') }}
        </badge>
        <badge v-if="showAdvertisingCode" class="o-product-card-horizontal__badge bg-primary" :class="{ 'a-badge--offset': isSki }">
          {{ $t(`ProductCard_Discount_${advertisingCode}`, advertisingCode) }}
        </badge>
        <base-icon v-if="showCareIcon" icon="care-icon" class="o-product-card-horizontal__care-icon a-care-icon p-3" />
      </base-link>

      <base-link :link="product.url" class="o-product-card-horizontal__content" @click.native="onProductClick">
        <slot name="title">
          <div class="bold small o-product-card-horizontal__brand">
            {{ $isNotNullOrUndefined(getPropertyValue('brand')) ? getPropertyValue('brand') : '' }}
          </div>
          <p v-if="$isNotNullOrUndefined(getPropertyValue('description1'))" class="o-product-card-horizontal__title h5">
            {{ getPropertyValue('description1') }}
          </p>
        </slot>
        <slot name="description">
          <div class="o-product-card-horizontal__description">
            <div v-if="$isNotNullOrUndefined(getPropertyValue('description2'))">{{ getPropertyValue('description2') }}</div>
            <div v-if="$isNotNullOrUndefined(getPropertyValue('description3'))">{{ getPropertyValue('description3') }}</div>
            <div v-if="$isNotNullOrUndefined(product.sku)" class="mt-2">
              {{ $t('productId', 'Varenr.') }} <clip-board>{{ product.sku }}</clip-board>
            </div>
            <stock :sku="product.sku" :delivery-time="deliveryTime" :is-ordering-item="isOrderingItem" :stock="product.stockInfo" />
          </div>
        </slot>
      </base-link>
      <div class="o-product-card-horizontal__actions-wrapper">
        <div class="o-product-card-horizontal__energy-label">
          <base-link v-if="energyLabel" :link="energyLabelDownload.link" target="_blank" class="o-product-card-horizontal__energy-link">
            <base-image
              :url="`/energy-labels/${energyLabel.toLowerCase().replace(/\+/g, 'p')}.png`"
              class="o-product-card-horizontal__energy-picture"
              image-class="o-product-card-horizontal__energy-image"
              :width="65"
              :height="50"
              :include-cdn="false"
              :is-lazy-load="false"
            />
          </base-link>
        </div>
        <div>
          <div class="o-product-card-horizontal__unit-price">
            <slot name="price">
              <price
                v-if="product.price && product.price.price > 0"
                class="o-product-card-horizontal__price"
                :price="product.price"
                :product="product"
                :is-logged-in="isLoggedIn"
                :is-product-on-discount="isProductOnDiscount"
                :is-lavpris="!isLoggedIn && advertisingCode && advertisingCode === 'LAVPRIS'"
              />
            </slot>
            <div v-if="isRestSalg" class="o-product-card-horizontal__while-available">
              {{ $t('ProductCard_AvailableWhileInStock', 'Så længe laver haves') }}
            </div>
          </div>
          <div v-if="brandPriceHidden()">
            {{ $t('loginToSeePrice', 'Log ind for at se pris') }}
          </div>
          <div
            v-if="!brandPriceHidden() && showAddToCartButton"
            class="o-product-card-horizontal__actions"
            :class="{ invisible: product.price.price === 0 && !isVivaldiUser }"
          >
            <quantity-input-compact
              :id="product.id"
              class="mr-4"
              :min-quantity="kolliNumber"
              :kolli="kolliNumber"
              @quantity-change="onQuantityChange"
            />
            <add-to-cart
              :sku="product.sku"
              :quantity="quantity"
              :is-ordering-item="isOrderingItem"
              :is-restsalg-item="isRestSalg"
              :disabled="brandPriceHidden() || (product.price.price === 0 && !isVivaldiUser)"
              class="o-product-card-horizontal__add-to-cart"
            />
          </div>
        </div>
      </div>

      <div class="o-product-card-horizontal__action-icons">
        <base-icon v-if="isProductOnLatestBoughtProductsList" icon="latestbought" class="action-icons__icon text-body" />
        <slot name="wishlist-icon">
          <wishlist-button
            v-if="hasCustomerNumber"
            class="o-product-card-horizontal__wishlist action-icons__icon"
            :product-id="product.sku"
          />
        </slot>
      </div>
    </span>
  </div>
</template>
<script>
import ClipBoard from '@/components/atoms/ClipBoard'
import WishlistButton from '@/components/molecules/ShopCommerce/WishlistButton.vue'
import Price from '@/components/atoms/Price.vue'
import BaseImage from '@/components/atoms/BaseImage.vue'
import BaseLink from '@/components/atoms/BaseLink.vue'
import AddToCart from '@/components/molecules/AddToCart.vue'
import { trackProductClick, trackAddToCart } from '@/utils/enhancedEcomTracking'
import { getKolliNumber } from '@/utils/ShopCommerce/product-properties'
import LoginMixin from '@/mixins/loginMixin'
import QuantityInputCompact from '@/components/molecules/QuantityInputCompact'
import BaseIcon from '@/components/atoms/BaseIcon'
import EnergyLabel from '@/mixins/energyLabelMixin'
import Stock from '~/components/molecules/ShopCommerce/StockStatus'
import Badge from '@/components/atoms/Badge'

export default {
  name: 'ProductCardHorizontal',
  components: {
    ClipBoard,
    BaseLink,
    BaseImage,
    Price,
    AddToCart,
    WishlistButton,
    QuantityInputCompact,
    BaseIcon,
    Stock,
    Badge
  },
  mixins: [LoginMixin, EnergyLabel],
  props: {
    /**
     * Product image
     * It should be an url of the product
     */
    product: {
      type: Object,
      default() {
        return {}
      },
      required: true
    },
    /**
     * Badge color
     * It can be according to our standard colors, or legitimate CSS color such as `#fff`, `rgb(255,255,255)`), and `lightgray` or nothing.
     * Standard colors: `primary`, `secondary`, `white`, `black`, `accent`, `green-primary`, `green-secondary`, `gray-primary`, `gray-secondary`, `light-primary`, `light-secondary`, `pink-primary`, `pink-secondary`, `yellow-primary`, `yellow-secondary`, `blue-primary`, `blue-secondary`.
     */
    badgeColor: {
      type: String,
      default: ''
    },
    /**
     * Status of showing badge
     */
    showBadge: {
      type: Boolean,
      default: true
    },
    /**
     * Status of showing add to cart button
     */
    showAddToCartButton: {
      type: Boolean,
      default: false
    },
    /**
     * addToCartDisabled status of whether button is disabled when out of stock
     */
    addToCartDisabled: {
      type: Boolean,
      default: false
    },
    /**
     * Wether to show wishlist icon or not
     */
    showWishListIcon: {
      type: Boolean,
      default: true
    },
    isRecommended: {
      type: Boolean,
      default: false
    },
    isSearchResult: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      quantity: 1,
      isProductOnDiscount: false
    }
  },
  computed: {
    showCareIcon() {
      return this.product.properties.filter((x) => x.name === 'broennum_care' && x.value === 'Ja').length > 0
    },
    placeholder() {
      return this.$store.getters['site/getPlaceholder']
    },
    siteSettings() {
      return this.$store.getters['site/getSiteSettings']
    },
    siteSettings() {
      return this.$store.getters['site/getSiteSettings']
    },
    kolliNumber() {
      return getKolliNumber(this.product)
    },
    hasCustomerNumber() {
      return this.$store.getters['user/user/hasCustomerNumber']
    },
    latestBoughtProductsList() {
      return this.$store.getters['user/user/getLatestBoughtProductsList']
    },
    isProductOnLatestBoughtProductsList() {
      return this.latestBoughtProductsList.includes(this.product.sku)
    },
    user() {
      return this.$store.getters['user/user/getUser']
    },
    isSki() {
      return this.user?.accountProperties?.sKIAccount === 1 && this.getPropertyValue('skiitem') === 'TRUE'
    },
    isVivaldiUser() {
      return this.user?.accountProperties?.vivaldi === 1
    },
    currency() {
      return this.$store.getters['currency/getCurrentCurrency']
    },
    advertisingCode() {
      return this.getPropertyValue('advertisingcode')
    },
    isOrderingItem() {
      return this.getPropertyValue('orderingitem') === 'TRUE'
    },
    isRestSalg() {
      return (this.advertisingCode && this.advertisingCode === 'RESTSALG') ?? false
    },
    showAdvertisingCode() {
      return this.advertisingCode &&
        this.$t(`ProductCard_Discount_${this.advertisingCode}`) !== `ProductCard_Discount_${this.advertisingCode}` &&
        this.$t(`ProductCard_Discount_${this.advertisingCode}`) !== ''
        ? true
        : false
    },
    deliveryTime() {
      return parseFloat(this.getPropertyValue('deliverytimeoutofstock'))
    },
    sOrderingItem() {
      return this.getPropertyValue('orderingitem') === 'TRUE'
    }
  },
  mounted() {
    this.quantity = this.kolliNumber
    this.isProductOnDiscount = this.isPriceOnDiscount(this.product.price)

    if (this.hasCustomerNumber) {
      this.$store.dispatch('user/user/fetchLatestBoughtProductsList')
    }
  },
  methods: {
    onProductClick() {
      this.$emit('click-card')
      trackProductClick(this.product, this.product?.primaryCategoryName)
    },
    onQuantityChange(payload) {
      this.quantity = payload.quantity
    },
    onAddToCart() {
      trackAddToCart(this.product, this.quantity, this.currency)
    },
    getPropertyValue(propertyName) {
      let item = this.product.properties.find((x) => x.name.toLowerCase() === propertyName.toLowerCase() && x.value !== '')
      return item?.value
    },
    isPriceOnDiscount(price) {
      return price?.price < price?.regular
    },
    brandPriceHidden() {
      const hiddenBrands = this.siteSettings?.hiddenBrands ?? []
      return hiddenBrands.map((str) => str.toLowerCase()).includes(this.getPropertyValue('brand')?.toLowerCase()) && !this.isLoggedIn
    }
  }
}
</script>
